import { useAuthStore } from './auth/useAuthStore';
import type { Role } from '../types/domain';

export interface PermissionConfig {
  roles?: Role[];
}

/**
 * UI-level permission checks. Mirrors the backend's role model: `admin` is the
 * super-user and passes every gate. Real enforcement is server-side (gateway
 * route-role map + [RequireRoles]); this only decides what to render.
 */
export const usePermission = () => {
  const hasRole = useAuthStore((s) => s.hasRole);
  const isAdmin = useAuthStore((s) => s.isAdmin);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  const can = (config: PermissionConfig | Role): boolean => {
    if (!isAuthenticated()) return false;
    if (isAdmin()) return true;
    if (typeof config === 'string') return hasRole(config);
    if (config.roles?.length) return hasRole(config.roles);
    return true;
  };

  return {
    can,
    canView: can,
    canCreate: (config: PermissionConfig | Role) => can(config),
    canEdit: (config: PermissionConfig | Role) => can(config),
    canDelete: (config: PermissionConfig | Role) => can(config),
    isAdmin: isAdmin(),
    // manager+ can manage operational data; workers/customers cannot
    canManage: hasRole(['manager']),
  };
};
